import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Collapse, LinearProgress, Paper } from '@mui/material';
import { memo, useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import ProductAvailabilityChip from '../../../components/Product/ProductAvailabilityChip';
import LangEnum from '../../../lang/LangEnum';
import { useSalesOpportunityCategories } from '../../../services/modules/orderbook/orderbook.queries';
import { formatDate } from '../../../utils/dates';
import OrderChip from '../../orders/list/OrderChip';
import { CompactAvailability } from '../../products/ProductTypes';
import { SalesOpportunity } from '../../sales-opportunity/salesOpportunity.types';
import { useTranslation } from '../../translation/TranslationContext';
import { useOrderBookStyles } from '../styles/OrderBookStyles';
import SalesOpportunityCategoryList from './SalesOpportunityCategoryList';

interface SalesOpportunityDetailsProps {
  open: boolean;
  salesOpportunity: SalesOpportunity;
  testId: string;
}

const useStyles = makeStyles()((theme) => ({
  chip: {
    padding: theme.spacing(0.25),
    minHeight: 24,
  },
  listContainer: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
}));

const SalesOpportunityDetails = ({
  open,
  salesOpportunity,
  testId,
}: SalesOpportunityDetailsProps) => {
  const { translate } = useTranslation();
  const { classes } = useOrderBookStyles();
  const { classes: styles } = useStyles();

  const listScrollParent = useRef<HTMLElement | null>();

  useEffect(() => {
    const containerRef = document.getElementById('order-book-container');

    listScrollParent.current = containerRef;
  }, []);

  const { data, fetchMore, refetch, isFetching } = useSalesOpportunityCategories({
    salesOpportunityId: salesOpportunity.id,
    shouldFetch: open,
  });

  useEffect(() => {
    if (data.list.length === 0 && open) {
      refetch();
    }
  }, [open]);

  const salesOpportunityHasDeliveryWindows =
    salesOpportunity?.deliveryWindows && salesOpportunity.deliveryWindows?.length > 0;

  const filterAvailabilities = () => {
    if (salesOpportunityHasDeliveryWindows) {
      return salesOpportunity.availability.filter(
        (availability) => availability !== CompactAvailability.PRE_ORDER,
      );
    }

    return salesOpportunity.availability;
  };

  return (
    <Collapse in={open} mountOnEnter unmountOnExit>
      <Paper elevation={0} test-id={testId} sx={{ visibility: open ? 'visible' : 'hidden' }}>
        <Box
          className={classes.horizontalPadding}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
            marginTop: 1,
          }}
        >
          <Typography variant="body2">
            {translate(LangEnum.GLOBAL_OPPORTUNITY)} {salesOpportunity.code}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              flexWrap: 'wrap',
            }}
          >
            {salesOpportunity.startDate && salesOpportunity.endDate && (
              <OrderChip
                name={translate(LangEnum.GLOBAL_FROM_TO_DATE, {
                  startDate: formatDate(salesOpportunity.startDate),
                  endDate: formatDate(salesOpportunity.endDate),
                })}
                className={styles.chip}
              />
            )}

            {salesOpportunity?.collections?.map((collection) => (
              <OrderChip name={collection.name} className={styles.chip} />
            ))}

            {salesOpportunity.deliveryWindows?.map((deliveryWindow) => (
              <OrderChip
                key={deliveryWindow.name}
                name={deliveryWindow.name}
                className={styles.chip}
              />
            ))}

            {filterAvailabilities().map((availability) => (
              <ProductAvailabilityChip key={availability} availability={availability} />
            ))}

            {salesOpportunity.classificationsNames
              ?.filter((names) => names)
              ?.map((classification) => (
                <OrderChip key={classification} name={classification} className={styles.chip} />
              ))}
          </Box>
        </Box>

        <Box className={styles.listContainer}>
          {listScrollParent.current && (
            <SalesOpportunityCategoryList
              list={data.list}
              loadMoreRows={fetchMore}
              listScrollParent={listScrollParent.current}
            />
          )}
          {isFetching && <LinearProgress />}
        </Box>
      </Paper>
    </Collapse>
  );
};

export default memo(SalesOpportunityDetails);
